import { AxiosProgressEvent } from 'axios';
import { httpClient, basicAxiosInstance } from 'providers/clients/httpClient';

export type VmsUploadResult = {
  vms_upload_url: string;
};

type VmsUploadOptions = {
  file: File;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  signal?: AbortController['signal'];
};

/**
 * Checks whether a File is a video type by its MIME type.
 */
export const isVideoFile = (file: File): boolean => {
  return Boolean(file?.type?.startsWith('video/'));
};

/**
 * Uploads a video file to VMS (Field59) via the S3 flow.
 *
 * 1. POST /editorial/video/vms/ to get S3 form data + vms_upload_url
 * 2. Upload the file to S3 using the returned form attributes
 * 3. Return the vms_upload_url for use when creating the asset
 */
export const uploadVideoToVms = async ({
  file,
  onUploadProgress,
  signal,
}: VmsUploadOptions): Promise<VmsUploadResult> => {
  // Step 1: Get VMS upload config from the backend
  const { data } = await httpClient({
    url: '/editorial/video/vms/',
    method: 'POST',
    data: JSON.stringify({ name: file?.name, type: 'video' }),
    signal,
  });

  if (data?.status !== 'success') {
    const vmsMessage = data?.message || 'Unspecified error from VMS';
    throw new Error(String(vmsMessage));
  }

  const { form_attributes, form_fields, vms_upload_url } = data?.data;

  // Step 2: Upload the file to the S3 bucket
  const s3FormData = new FormData();
  Object.entries(form_fields).forEach(([key, value]) => {
    s3FormData.append(key, value as string);
  });
  s3FormData.append('file', file);

  // Use basicAxiosInstance to avoid interceptors that would add auth headers
  await httpClient(
    {
      url: form_attributes.action,
      method: form_attributes.method,
      data: s3FormData,
      onUploadProgress,
      signal,
    },
    basicAxiosInstance,
  );

  return { vms_upload_url };
};
